import { Grid } from '@material-ui/core';
import { Card, SectionHeader, Body1, Body2 } from '../../blocks';
import { css } from '@emotion/react';
import { Image } from 'react-bootstrap';
import { colors } from '@/styles';
import FileDropZone from '@/components/OrderDetailsPage/FileDropZone';
import uploadPhotoAsync from '@/utilities/uploadPhotoAsync';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { formatInTimeZone } from 'date-fns-tz';
import { asDateInTZ } from '@/utilities/convertToISO';
import { useMemo } from 'react';

const SupportingDocumentsCard = ({ claim, callbacks, isEditable }) => {
    const { user_id, username } = useClientUser();

    const userTimezone = useMemo(() => {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, []);

    const formatCreatedAt = useMemo(() => {
        return (createdAt) => {
            if (!createdAt) return 'N/A';
            return formatInTimeZone(asDateInTZ(createdAt, userTimezone), userTimezone, 'MM/dd/yyyy h:mm a');
        };
    }, [userTimezone]);

    const handleFileAdd = async (filesArray) => {
        try {
            const uploadedDocuments = [];

            for (const { file, fileType } of filesArray) {
                const fileUrl = URL.createObjectURL(file);
                const fbFileUrl = await uploadPhotoAsync(fileUrl, 'claims', fileType);

                uploadedDocuments.push({
                    url: fbFileUrl,
                    environment: 'web',
                    user_id: user_id,
                    username: username || 'Unknown',
                    created_at: new Date().toISOString(),
                });
            }

            const existingDocuments = claim?.supporting_documents || [];
            callbacks.updateClaim({
                supporting_documents: [...existingDocuments, ...uploadedDocuments],
            });
        } catch (e) {
            console.error(e);
            captureException(e);
            callbacks.setNotification({
                severity: 'error',
                message: 'Failed to upload supporting document.',
            });
        }
    };

    const supportingDocuments = claim?.supporting_documents || [];

    return (
        <Card>
            <Grid
                item
                css={css`
                    margin-bottom: 1rem;
                `}
            >
                <SectionHeader>Additional Supporting Documents and Images</SectionHeader>
            </Grid>

            {supportingDocuments.length > 0 && (
                <Grid
                    container
                    direction="column"
                    css={css`
                        gap: 1rem;
                        margin-bottom: 1rem;
                    `}
                >
                    {supportingDocuments.map((doc, index) => (
                        <Grid
                            container
                            direction="row"
                            key={index}
                            css={css`
                                align-items: center;
                                gap: 1rem;
                            `}
                        >
                            <Grid item>
                                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                    <Image
                                        src={doc.url}
                                        css={css`
                                            height: 100px;
                                            width: 100px;
                                            object-fit: cover;
                                            border-radius: 4px;
                                        `}
                                    />
                                </a>
                            </Grid>
                            <Grid
                                item
                                css={css`
                                    flex: 1;
                                `}
                            >
                                <Grid
                                    container
                                    direction="column"
                                    css={css`
                                        gap: 0.25rem;
                                    `}
                                >
                                    <Body1>Uploaded by: {doc.username || 'Unknown'}</Body1>
                                    {doc.created_at && <Body2>Uploaded: {formatCreatedAt(doc.created_at)}</Body2>}
                                </Grid>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            )}

            {isEditable && (
                <Grid item>
                    <FileDropZone
                        type="multiple"
                        customLabel="Add Supporting Documents"
                        handleFileAdd={handleFileAdd}
                        disabled={!isEditable}
                    />
                </Grid>
            )}
        </Card>
    );
};

export default SupportingDocumentsCard;
